import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { HttpService } from 'src/app/services/http.service';
import { FeatureFlag } from 'src/app/bo/featureFlag';
import { UserDtoLight } from 'src/app/bo/user/user';
import { Site } from 'src/app/bo/site';

export const AREA_FEATURE_NAME = 'show-area-page';

export function getOrganizationIdsFromSites(sites: Site[]): number[] {
  return Array.from(
    new Set(
      sites
        .map(s => s.region?.orgId)
        .filter((id): id is number => id != null),
    ),
  );
}

export function isAreaEnabledForUser(flag: FeatureFlag | undefined, organizationIds: number[]): boolean {
  if (!flag) return false;
  if (flag.isEnabled) return true;

  const allowed = flag.organization
    ?.split(',')
    .map(s => s.trim())
    .filter(s => s.length > 0) ?? [];

  if (allowed.length === 0) return false;

  return organizationIds.some(orgId => allowed.includes(String(orgId)));
}

export const LandingRedirectGuard: CanActivateFn = () => {
  const http = inject(HttpService);
  const router = inject(Router);

  return forkJoin({
    flags: http.get<FeatureFlag[]>('FeatureFlag').pipe(catchError(() => of([] as FeatureFlag[]))),
    user: http.get<UserDtoLight>('Users/CurrentUser').pipe(catchError(() => of(null as UserDtoLight | null))),
    sites: http.get<Site[]>('Sites/Active').pipe(catchError(() => of([] as Site[]))),
  }).pipe(
    map(({ flags, user, sites }): UrlTree => {

      const flag = flags.find(f => f.featureName === AREA_FEATURE_NAME);
      const organizationIds = getOrganizationIdsFromSites(sites);

      if (isAreaEnabledForUser(flag, organizationIds)) {
        return router.parseUrl('/area');
      }

      const firstSiteId = sites.length > 0 ? sites[0].siteId : null;
      return firstSiteId != null
        ? router.parseUrl(`/mysite/${firstSiteId}`)
        : router.parseUrl('/sites');
    }),
  );
};
